const MB = 1024 * 1024;

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'tif', 'tiff', 'gif'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov'];
const AUDIO_EXTENSIONS = ['wav', 'mp3'];

const MAX_REFERENCE_IMAGES = 9;
const MAX_REFERENCE_VIDEOS = 3;
const MAX_REFERENCE_AUDIOS = 3;
const MAX_REQUEST_SIZE = 64 * MB;
const IMAGE_MAX_SIZE = 30 * MB;
const VIDEO_MAX_SIZE = 50 * MB;
const IMAGE_MIN_EDGE = 300;
const IMAGE_MAX_EDGE = 6000;
const VIDEO_MIN_EDGE = 300;
const VIDEO_MAX_EDGE = 6000;
const MIN_RATIO = 0.4;
const MAX_RATIO = 2.5;
const VIDEO_MIN_DURATION = 2;
const VIDEO_MAX_DURATION = 15.49;
const AUDIO_MIN_DURATION = 2;
const AUDIO_MAX_DURATION = 15;
const VIDEO_MIN_PIXELS = 409600;
const VIDEO_MAX_PIXELS = 927408;
const VIDEO_MIN_FPS = 24;
const VIDEO_MAX_FPS = 60;
const MAX_TOTAL_MEDIA_DURATION = 15;
const VIDEO_DURATION_REJECT_THRESHOLD = 15.5;

const DEFAULT_REFERENCE_PROFILE = {
  maxImages: MAX_REFERENCE_IMAGES,
  maxVideos: MAX_REFERENCE_VIDEOS,
  maxAudios: MAX_REFERENCE_AUDIOS,
  videoMaxDuration: VIDEO_MAX_DURATION,
  audioMaxDuration: AUDIO_MAX_DURATION,
  maxVideoPixels: VIDEO_MAX_PIXELS,
  maxTotalDuration: MAX_TOTAL_MEDIA_DURATION,
};

const SEEDANCE_25_REFERENCE_PROFILE = {
  ...DEFAULT_REFERENCE_PROFILE,
  maxImages: 30,
  maxVideos: 10,
  maxAudios: 10,
  videoMaxDuration: 30,
  audioMaxDuration: 30,
  maxVideoPixels: 8295044,
  maxTotalDuration: 30,
};

export function getCreatorMediaValidationProfile(model) {
  return model === 'Doubao-Seedance-2.5'
    ? SEEDANCE_25_REFERENCE_PROFILE
    : DEFAULT_REFERENCE_PROFILE;
}

export function isAllowedVideoDurationSeconds(duration) {
  return duration >= VIDEO_MIN_DURATION && duration < VIDEO_DURATION_REJECT_THRESHOLD;
}

export function isAllowedTotalReferenceMediaDurationSeconds(duration) {
  return Math.round(duration) <= MAX_TOTAL_MEDIA_DURATION;
}

function getExtension(file) {
  const parts = (file.name || '').toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
}

function formatBytes(bytes) {
  return `${(bytes / MB).toFixed(1)} MB`;
}

function getFileCategory(file) {
  const extension = getExtension(file);
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
  if (VIDEO_EXTENSIONS.includes(extension)) return 'video';
  if (AUDIO_EXTENSIONS.includes(extension)) return 'audio';
  return 'unknown';
}

function validateAspectRatio(width, height, label) {
  const ratio = width / height;
  if (ratio < MIN_RATIO || ratio > MAX_RATIO) {
    throw new Error(`${label}宽高比需在 ${MIN_RATIO}-${MAX_RATIO} 之间`);
  }
}

function validateEdgeLength(width, height, min, max, label) {
  if (width < min || width > max || height < min || height > max) {
    throw new Error(`${label}宽高需在 ${min}-${max}px 之间`);
  }
}

function loadImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const result = { width: image.naturalWidth, height: image.naturalHeight };
      URL.revokeObjectURL(objectUrl);
      resolve(result);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('无法读取图片尺寸'));
    };
    image.src = objectUrl;
  });
}

function loadMediaElement(file, tagName) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const element = document.createElement(tagName);
    element.preload = 'metadata';
    element.onloadedmetadata = () => {
      resolve({ element, objectUrl });
    };
    element.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`无法读取${tagName === 'video' ? '视频' : '音频'}元数据`));
    };
    element.src = objectUrl;
  });
}

async function estimateVideoFps(video) {
  if (typeof video.requestVideoFrameCallback !== 'function') {
    return null;
  }

  const sampleTarget = Math.min(video.duration || 0, 1.2);
  if (sampleTarget < 0.2) {
    return null;
  }

  return new Promise((resolve) => {
    let frameCount = 0;
    let firstMediaTime = null;
    let lastMediaTime = null;
    let settled = false;

    const finish = (fps) => {
      if (settled) return;
      settled = true;
      video.pause();
      resolve(fps);
    };

    const onFrame = (_now, metadata) => {
      if (settled) return;
      frameCount += 1;
      if (firstMediaTime === null) {
        firstMediaTime = metadata.mediaTime;
      }
      lastMediaTime = metadata.mediaTime;

      const elapsed = (lastMediaTime || 0) - (firstMediaTime || 0);
      if (elapsed >= Math.min(sampleTarget, 1) || frameCount >= 120) {
        finish(elapsed > 0 ? frameCount / elapsed : null);
        return;
      }
      video.requestVideoFrameCallback(onFrame);
    };

    const timeoutId = window.setTimeout(() => {
      finish(
        firstMediaTime !== null && lastMediaTime !== null && lastMediaTime > firstMediaTime
          ? frameCount / (lastMediaTime - firstMediaTime)
          : null
      );
    }, 2500);

    video.requestVideoFrameCallback((_now, metadata) => {
      window.clearTimeout(timeoutId);
      onFrame(_now, metadata);
    });

    video.currentTime = 0;
    video.muted = true;
    video.playsInline = true;
    video
      .play()
      .catch(() => {
        finish(null);
      });
  });
}

async function getVideoMetadata(file) {
  const { element: video, objectUrl } = await loadMediaElement(file, 'video');
  try {
    const fps = await estimateVideoFps(video);
    return {
      width: video.videoWidth,
      height: video.videoHeight,
      duration: video.duration,
      fps,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function getAudioMetadata(file) {
  const { element: audio, objectUrl } = await loadMediaElement(file, 'audio');
  try {
    return { duration: audio.duration };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function getAggregateStats(items, category) {
  return (items || []).reduce(
    (acc, item) => {
      if (item?.mediaType === category) {
        acc.count += 1;
        acc.totalDuration += item.duration || 0;
        acc.totalSize += item.fileSize || 0;
      }
      return acc;
    },
    { count: 0, totalDuration: 0, totalSize: 0 }
  );
}

export async function validateCreatorFile(file, options = {}) {
  const { scope = 'reference', existingItems = [], model } = options;
  const profile = getCreatorMediaValidationProfile(model);
  const category = getFileCategory(file);

  if (category === 'unknown') {
    throw new Error('不支持的文件格式');
  }

  if (scope === 'image-only' && category !== 'image') {
    throw new Error('这里只能上传图片文件');
  }

  if (scope === 'reference') {
    const catLimits = {
      image: profile.maxImages,
      video: profile.maxVideos,
      audio: profile.maxAudios,
    };
    const maxForCat = catLimits[category] || 3;
    const stats = getAggregateStats(existingItems, category);
    if (stats.count >= maxForCat) {
      const catLabel = category === 'image' ? '图片' : category === 'video' ? '视频' : '音频';
      throw new Error(`参考${catLabel}最多上传 ${maxForCat} 个`);
    }
  }

  if (scope === 'reference') {
    const totalKnownSize = existingItems.reduce((sum, item) => sum + (item.fileSize || 0), 0);
    if (totalKnownSize + file.size > MAX_REQUEST_SIZE) {
      throw new Error(`本地已选素材总大小不能超过 ${formatBytes(MAX_REQUEST_SIZE)}`);
    }
  }

  if (category === 'image') {
    if (file.size >= IMAGE_MAX_SIZE) {
      throw new Error(`单张图片需小于 ${formatBytes(IMAGE_MAX_SIZE)}`);
    }

    const { width, height } = await loadImageDimensions(file);
    validateEdgeLength(width, height, IMAGE_MIN_EDGE, IMAGE_MAX_EDGE, '图片');
    validateAspectRatio(width, height, '图片');

    return {
      mediaType: 'image',
      fileSize: file.size,
      width,
      height,
    };
  }

  if (category === 'video') {
    if (file.size > VIDEO_MAX_SIZE) {
      throw new Error(`单个视频不能超过 ${formatBytes(VIDEO_MAX_SIZE)}`);
    }

    const { width, height, duration, fps } = await getVideoMetadata(file);
    validateEdgeLength(width, height, VIDEO_MIN_EDGE, VIDEO_MAX_EDGE, '视频');
    validateAspectRatio(width, height, '视频');

    const pixels = width * height;
    if (pixels < VIDEO_MIN_PIXELS || pixels > profile.maxVideoPixels) {
      throw new Error(`视频画面像素需在 ${VIDEO_MIN_PIXELS}-${profile.maxVideoPixels} 之间`);
    }

    if (duration < VIDEO_MIN_DURATION || duration > profile.videoMaxDuration) {
      throw new Error(`单个视频时长需在 ${VIDEO_MIN_DURATION}-${profile.videoMaxDuration} 秒之间`);
    }

    if (fps !== null && (fps < VIDEO_MIN_FPS || fps > VIDEO_MAX_FPS)) {
      throw new Error(`视频帧率需在 ${VIDEO_MIN_FPS}-${VIDEO_MAX_FPS} FPS 之间`);
    }

    if (scope === 'reference') {
      const stats = getAggregateStats(existingItems, 'video');
      if (stats.count + 1 > profile.maxVideos) {
        throw new Error(`参考视频最多上传 ${profile.maxVideos} 个`);
      }
      if (Math.round(stats.totalDuration + duration) > profile.maxTotalDuration) {
        throw new Error(`参考视频总时长不能超过 ${profile.maxTotalDuration} 秒`);
      }
    }

    return {
      mediaType: 'video',
      fileSize: file.size,
      width,
      height,
      duration,
      fps,
    };
  }

  const { duration } = await getAudioMetadata(file);
  if (duration < AUDIO_MIN_DURATION || duration > profile.audioMaxDuration) {
    throw new Error(`单个音频时长需在 ${AUDIO_MIN_DURATION}-${profile.audioMaxDuration} 秒之间`);
  }

  if (scope === 'reference') {
    const stats = getAggregateStats(existingItems, 'audio');
    if (stats.count + 1 > profile.maxAudios) {
      throw new Error(`参考音频最多上传 ${profile.maxAudios} 段`);
    }
    if (Math.round(stats.totalDuration + duration) > profile.maxTotalDuration) {
      throw new Error(`参考音频总时长不能超过 ${profile.maxTotalDuration} 秒`);
    }
  }

  return {
    mediaType: 'audio',
    fileSize: file.size,
    duration,
  };
}
